import { useTranslations } from 'next-intl'
import { useCallback, useMemo } from 'react'
import useClevertapForSrp from '../../analytics/clevertap/srp'
import useUpdateSearchResultCache from '../updateSearchResultCache'
import useSearchResults from '../useSearchResults'

const usePriceRangeFilterManager = (journeyLeg: 0 | 1 = 0) => {
    const t = useTranslations('common')
    const { triggerSrpEvent } = useClevertapForSrp()

    const { filterData, selectedJourneyLeg } = useSearchResults(journeyLeg)
    const updateCache = useUpdateSearchResultCache()
    const { journeyPrice } = filterData || {}
    const targetJourneyPrice = journeyPrice?.[selectedJourneyLeg]
    const { min: defaultMin, max: defaultMax, current: currentPriceRange } = targetJourneyPrice || {}
    const { min: currentMin, max: currentMax } = currentPriceRange || {}

    const changePriceRange = useCallback(
        (min: number, max: number) => {
            triggerSrpEvent('Air_UI_Action', {
                action_name: 'filter',
                action_type: 'price',
                action_value: `${min}-${max}`
            })

            updateCache(currentData => {
                if (currentData?.filterData?.journeyPrice?.[selectedJourneyLeg]) {
                    currentData.filterData.journeyPrice[selectedJourneyLeg].current = { min, max }
                }
                return currentData as NonNullable<typeof currentData>
            })
        },
        [updateCache, selectedJourneyLeg, triggerSrpEvent]
    )

    const handleReset = useCallback(() => {
        const numDefaultMin = Number(defaultMin)
        const numDefaultMax = Number(defaultMax)
        if (!isNaN(numDefaultMin) && !isNaN(numDefaultMax)) {
            triggerSrpEvent('Air_UI_Action', {
                action_name: 'filter',
                action_type: 'reset',
                action_value: 'price'
            })

            updateCache(currentData => {
                if (currentData?.filterData?.journeyPrice?.[selectedJourneyLeg]) {
                    currentData.filterData.journeyPrice[selectedJourneyLeg].current = {
                        min: numDefaultMin,
                        max: numDefaultMax
                    }
                }
                return currentData as NonNullable<typeof currentData>
            })
        }
    }, [updateCache, selectedJourneyLeg, defaultMin, defaultMax, triggerSrpEvent])

    const isPriceRangeFilterApplied = useMemo(() => {
        return currentMin !== defaultMin || currentMax !== defaultMax
    }, [currentMin, currentMax, defaultMin, defaultMax])

    return {
        currentMin,
        currentMax,
        min: defaultMin,
        max: defaultMax,
        changePriceRange,
        handleReset,
        title: t('price'),
        isPriceRangeFilterApplied
    }
}

export default usePriceRangeFilterManager
